<!-- Edit Action Plan Modal -->
<ng-template #editPlanModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title font-weight-bold">{{ "Edit Action Plan" | translate }} — {{ "Day" | translate }} {{ editPlanItem?.day }}</h5>
    <button type="button" class="close" (click)="modal.dismiss()"><span>&times;</span></button>
  </div>
  <div class="modal-body">
    <div class="form-group mb-0">
      <label class="font-weight-bold">{{ "Content" | translate }}</label>
      <textarea class="form-control mt-1" rows="4" [(ngModel)]="editPlanContent" [placeholder]="'Enter new content' | translate"></textarea>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()" [disabled]="isUpdatingPlan">{{ "Cancel" | translate }}</button>
    <button type="button" class="btn btn-primary" (click)="onUpdatePlan(modal)" [disabled]="isUpdatingPlan || !editPlanContent.trim()">
      <span *ngIf="isUpdatingPlan" class="spinner-border spinner-border-sm mr-1" role="status"></span>
      {{ "Update" | translate }}
    </button>
  </div>
</ng-template>

<div class="wrapper-sign-up">
  <div class="section-title">
    <div class="row">
      <div class="col">
        <div class="d-flex align-items-center">
          <a routerLink="/dashboard/ai-landing-page" class="btn btn-back rounded-circle mt-2">
            <i class="fi-rr-arrow-alt-left"></i>
          </a>
          <div class="title">{{ "AI Landing Page" | translate }}</div>
        </div>
      </div>
    </div>
  </div>

  <div class="section-sign-up-step container-xl mt-4 px-4">

    <!-- Form -->
    <div class="card card-form p-4">
      <div class="form mt-3">
        <div class="form-group">
          <label class="form-label">{{ "Your Landing Page" | translate }}</label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="yourLandingPage"
            placeholder="{{ 'Enter your landing page URL or content' | translate }}"
          />
        </div>

        <div class="form-group">
          <label class="form-label">{{ "Competitor Landing Page" | translate }}</label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="competitorLandingPage"
            placeholder="{{ 'Enter competitor landing page URL or content' | translate }}"
          />
        </div>

        <div class="d-flex justify-content-end mt-3">
          <button class="btn btn-analyse" (click)="onSubmit()" [disabled]="!yourLandingPage || !competitorLandingPage || isSubmitting">
            <i class="fi fi-rr-sparkles mr-2" [class.fa-spin]="isSubmitting"></i>
            {{ (isSubmitting ? "Analysing..." : "Analyse") | translate }}
          </button>
        </div>
      </div>
    </div>

    <!-- AI Loading State -->
    <div class="ai-analyse-loading card p-4 mt-4" *ngIf="isSubmitting">
      <div class="text-center py-3">
        <div class="ai-spinner mb-3">
          <i class="fi fi-rr-sparkles fa-spin"></i>
        </div>
        <div class="font-weight-bold text-primary mb-1">
          {{ "Analysing your landing pages..." | translate }}
        </div>
        <div class="text-muted small">
          {{ "Comparing content, structure, and performance indicators. This may take a moment." | translate }}
        </div>
        <div class="ai-skeleton-box mt-4">
          <div class="skeleton-line full"></div>
          <div class="skeleton-line three-quarters"></div>
          <div class="skeleton-line half"></div>
          <div class="skeleton-line full mt-2"></div>
          <div class="skeleton-line three-quarters"></div>
        </div>
      </div>
    </div>

    <!-- Analysis Result -->
    <div class="analysis-result mt-4" *ngIf="analysis">

      <!-- Summary Banner -->
      <div class="summary-banner mb-4">
        <i class="fi fi-rr-document-signed banner-icon"></i>
        <p class="mb-0">{{ analysis.summary }}</p>
      </div>

      <!-- Score Cards -->
      <div class="row mb-4">
        <div class="col-md-6 mb-3 mb-md-0">
          <div class="score-card score-dealer">
            <div class="score-label">{{ "Your Page" | translate }}</div>
            <div class="score-number">{{ analysis.overall_score?.dealer }}<span class="score-denom">/10</span></div>
            <div class="score-bar">
              <div class="score-fill" [style.width.%]="analysis.overall_score?.dealer ? analysis.overall_score.dealer * 10 : 0"></div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="score-card score-competitor">
            <div class="score-label">{{ "Competitor" | translate }}</div>
            <div class="score-number">{{ analysis.overall_score?.competitor }}<span class="score-denom">/10</span></div>
            <div class="score-bar">
              <div class="score-fill" [style.width.%]="analysis.overall_score?.competitor ? analysis.overall_score.competitor * 10 : 0"></div>
            </div>
          </div>
        </div>
        <div class="col-12 mt-2" *ngIf="analysis.overall_score?.verdict">
          <p class="verdict-text">{{ analysis.overall_score?.verdict }}</p>
        </div>
      </div>

      <!-- Strengths & Weaknesses -->
      <div class="row mb-4" *ngIf="analysis.dealer_strengths?.length || analysis.dealer_weaknesses?.length || analysis.competitor_strengths?.length || analysis.competitor_weaknesses?.length">
        <div class="col-md-6 mb-3 mb-md-0">
          <div class="sw-card">
            <div class="sw-card-header dealer-header">
              <i class="fi fi-rr-user mr-2"></i>{{ "Your Page" | translate }}
            </div>
            <div class="sw-card-body">
              <div *ngIf="analysis.dealer_strengths?.length" class="mb-3">
                <div class="sw-section-label strengths-label"><i class="fi fi-rr-check-circle mr-1"></i>{{ "Strengths" | translate }}</div>
                <div *ngFor="let s of analysis.dealer_strengths" class="sw-item strength-item">
                  <i class="fi fi-rr-check mr-2 flex-shrink-0"></i><span>{{ s }}</span>
                </div>
              </div>
              <div *ngIf="analysis.dealer_weaknesses?.length">
                <div class="sw-section-label weaknesses-label"><i class="fi fi-rr-cross-circle mr-1"></i>{{ "Weaknesses" | translate }}</div>
                <div *ngFor="let s of analysis.dealer_weaknesses" class="sw-item weakness-item">
                  <i class="fi fi-rr-cross mr-2 flex-shrink-0"></i><span>{{ s }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="sw-card">
            <div class="sw-card-header competitor-header">
              <i class="fi fi-rr-users mr-2"></i>{{ "Competitor" | translate }}
            </div>
            <div class="sw-card-body">
              <div *ngIf="analysis.competitor_strengths?.length" class="mb-3">
                <div class="sw-section-label strengths-label"><i class="fi fi-rr-check-circle mr-1"></i>{{ "Strengths" | translate }}</div>
                <div *ngFor="let s of analysis.competitor_strengths" class="sw-item strength-item">
                  <i class="fi fi-rr-check mr-2 flex-shrink-0"></i><span>{{ s }}</span>
                </div>
              </div>
              <div *ngIf="analysis.competitor_weaknesses?.length">
                <div class="sw-section-label weaknesses-label"><i class="fi fi-rr-cross-circle mr-1"></i>{{ "Weaknesses" | translate }}</div>
                <div *ngFor="let s of analysis.competitor_weaknesses" class="sw-item weakness-item">
                  <i class="fi fi-rr-cross mr-2 flex-shrink-0"></i><span>{{ s }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detailed Analysis -->
      <div class="mb-4" *ngIf="analysis.above_fold_analysis || analysis.readability">
        <div class="detail-section-title"><i class="fi fi-rr-chart-histogram mr-2"></i>{{ "Detailed Analysis" | translate }}</div>
        <div class="row">
          <div class="col-md-6 mb-3" *ngIf="analysis.above_fold_analysis">
            <div class="analysis-card">
              <div class="analysis-card-title">{{ "Above Fold" | translate }}</div>
              <div class="analysis-row">
                <span class="analysis-label dealer-label">{{ "Your Page" | translate }}</span>
                <p class="mb-0">{{ analysis.above_fold_analysis.dealer }}</p>
              </div>
              <div class="analysis-row">
                <span class="analysis-label competitor-label">{{ "Competitor" | translate }}</span>
                <p class="mb-0">{{ analysis.above_fold_analysis.competitor }}</p>
              </div>
              <div class="analysis-verdict" *ngIf="analysis.above_fold_analysis.verdict">
                <i class="fi fi-rr-bulb mr-1"></i>{{ analysis.above_fold_analysis.verdict }}
              </div>
            </div>
          </div>
          <div class="col-md-6 mb-3" *ngIf="analysis.readability">
            <div class="analysis-card">
              <div class="analysis-card-title">{{ "Readability" | translate }}</div>
              <div class="analysis-row">
                <span class="analysis-label dealer-label">{{ "Your Page" | translate }} <span class="score-pill">{{ analysis.readability.dealer?.score }}</span></span>
                <p class="mb-0">{{ analysis.readability.dealer?.assessment }}</p>
              </div>
              <div class="analysis-row">
                <span class="analysis-label competitor-label">{{ "Competitor" | translate }} <span class="score-pill">{{ analysis.readability.competitor?.score }}</span></span>
                <p class="mb-0">{{ analysis.readability.competitor?.assessment }}</p>
              </div>
              <div class="analysis-verdict" *ngIf="analysis.readability.recommendation">
                <i class="fi fi-rr-bulb mr-1"></i>{{ analysis.readability.recommendation }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recommendations -->
      <div class="mb-4" *ngIf="analysis.missing_sections?.length || analysis.quick_wins?.length || analysis.critics?.length">
        <div class="detail-section-title"><i class="fi fi-rr-lightbulb-on mr-2"></i>{{ "Recommendations" | translate }}</div>
        <div class="row">
          <div class="col-md-4 mb-3" *ngIf="analysis.missing_sections?.length">
            <div class="reco-card reco-missing">
              <div class="reco-card-title"><i class="fi fi-rr-puzzle-piece mr-2"></i>{{ "Missing Sections" | translate }}</div>
              <div *ngFor="let s of analysis.missing_sections" class="reco-item">{{ s }}</div>
            </div>
          </div>
          <div class="col-md-4 mb-3" *ngIf="analysis.quick_wins?.length">
            <div class="reco-card reco-wins">
              <div class="reco-card-title"><i class="fi fi-rr-rocket mr-2"></i>{{ "Quick Wins" | translate }}</div>
              <div *ngFor="let s of analysis.quick_wins" class="reco-item">{{ s }}</div>
            </div>
          </div>
          <div class="col-md-4 mb-3" *ngIf="analysis.critics?.length">
            <div class="reco-card reco-critics">
              <div class="reco-card-title"><i class="fi fi-rr-exclamation mr-2"></i>{{ "Critics" | translate }}</div>
              <div *ngFor="let s of analysis.critics" class="reco-item">{{ s }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Improvements -->
      <div class="mb-4" *ngIf="analysis.improvements?.length">
        <div class="detail-section-title"><i class="fi fi-rr-arrow-trend-up mr-2"></i>{{ "Improvements" | translate }}</div>
        <div class="improvement-card" *ngFor="let item of analysis.improvements">
          <div class="improvement-header">
            <strong>{{ item.area }}</strong>
            <span class="priority-badge"
              [class.priority-high]="item.priority === 'high'"
              [class.priority-medium]="item.priority === 'medium'"
              [class.priority-low]="item.priority === 'low'">
              {{ item.priority }}
            </span>
          </div>
          <div *ngIf="item.current" class="improvement-row">
            <span class="improvement-label">{{ "Current" | translate }}</span>
            <span>{{ item.current }}</span>
          </div>
          <div class="improvement-row">
            <span class="improvement-label">{{ "Suggestion" | translate }}</span>
            <span>{{ item.suggestion }}</span>
          </div>
          <div class="improvement-reason" *ngIf="item.reason">{{ item.reason }}</div>
        </div>
      </div>

      <!-- Rewritten Copy -->
      <div class="mb-4" *ngIf="analysis.rewritten_copy">
        <div class="detail-section-title"><i class="fi fi-rr-pen-field mr-2"></i>{{ "Rewritten Copy" | translate }}</div>
        <div class="rewritten-copy-card">
          <div class="copy-row">
            <span class="copy-label">{{ "Headline" | translate }}</span>
            <span class="copy-value">{{ analysis.rewritten_copy.headline }}</span>
          </div>
          <div class="copy-row">
            <span class="copy-label">{{ "Subheadline" | translate }}</span>
            <span class="copy-value">{{ analysis.rewritten_copy.subheadline }}</span>
          </div>
          <div class="copy-row">
            <span class="copy-label">{{ "CTA" | translate }}</span>
            <span class="copy-value cta-value">{{ analysis.rewritten_copy.cta }}</span>
          </div>
        </div>
      </div>

      <!-- Action Plan -->
      <div *ngIf="analysis.action_plan" class="mb-4">
        <div class="detail-section-title"><i class="fi fi-rr-calendar-check mr-2"></i>{{ "Action Plan" | translate }}</div>
        <div *ngFor="let phase of getActionPlanPhases(analysis.action_plan)" class="mb-3">
          <div class="phase-toggle" (click)="togglePhase(phase.key)">
            <strong>{{ phase.label }}</strong>
            <i class="fi ml-2" [class.fi-rr-angle-down]="!actionPlanOpen[phase.key]" [class.fi-rr-angle-up]="actionPlanOpen[phase.key]"></i>
          </div>
          <div *ngIf="actionPlanOpen[phase.key]">
            <table class="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th style="width: 60px;">{{ "Day" | translate }}</th>
                  <th>{{ "Content" | translate }}</th>
                  <th *ngIf="showApplyButton" style="width: 70px;" class="text-center">{{ "Edit" | translate }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let item of phase.items">
                  <td class="text-center align-middle"><strong>{{ item.day }}</strong></td>
                  <td class="align-middle">
                    <div>{{ item.description }}</div>
                    <div class="text-muted small mt-1">{{ item.target }} &nbsp;|&nbsp; {{ item.type }}</div>
                    <div *ngIf="item.new_content" class="mt-1 text-primary small">{{ item.new_content }}</div>
                  </td>
                  <td *ngIf="showApplyButton" class="text-center align-middle">
                    <button type="button" class="btn btn-sm btn-outline-secondary" (click)="openEditPlan(item)">
                      <i class="fi fi-rr-pencil"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Apply Buttons -->
      <div class="d-flex justify-content-end mt-2 pb-2" *ngIf="showApplyButton">
        <button class="btn btn-outline-primary mr-2" (click)="onApply(2)" [disabled]="isApplying">
          <span *ngIf="isApplying && applyingType === 2" class="spinner-border spinner-border-sm mr-1" role="status"></span>
          {{ "Manual Apply" | translate }}
        </button>
        <button class="btn btn-primary" (click)="onApply(1)" [disabled]="isApplying">
          <span *ngIf="isApplying && applyingType === 1" class="spinner-border spinner-border-sm mr-1" role="status"></span>
          {{ "Auto Apply" | translate }}
        </button>
      </div>

    </div>

  </div>
</div>
